import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Portfolio() {
  useSeo({ title: "Portfolio", description: "A selection of Buddy Solution's recent projects across web, mobile, e-commerce and branding." });
  const { data: items = [] } = useQuery({ queryKey: ["portfolio"], queryFn: async () => (await api.get("/portfolio")).data });
  const cats = ["All", ...new Set(items.map((i) => i.category))];
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? items : items.filter((i) => i.category === active);

  return (
    <div className="bs-container py-16 lg:py-20">
      <Reveal><SectionHead center eyebrow="Portfolio" title="Work we're proud of"
        subtitle="Client names are kept confidential where required. Selected projects illustrate our range and craft." /></Reveal>

      <div className="mt-10 flex flex-wrap justify-center gap-2">
        {cats.map((c) => (
          <button key={c} onClick={() => setActive(c)} data-testid={`portfolio-filter-${c}`}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active === c ? "bg-primary text-primary-foreground" : "border border-border bg-card hover:bg-secondary"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.04}>
            <Link to={`/portfolio/${p.slug}`} data-testid={`portfolio-item-${p.slug}`}
              className="group flex h-full flex-col overflow-hidden bs-card hover:-translate-y-1 hover:shadow-soft">
              <div className="overflow-hidden">
                <img src={p.image} alt={p.title} className="aspect-[16/11] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{p.category}</Badge>
                  {p.is_case_study && <Badge className="bg-accent/15 text-accent hover:bg-accent/15">Case study</Badge>}
                </div>
                <h3 className="mt-3 font-display text-lg font-bold">{p.title}</h3>
                <p className="mt-1.5 flex-1 text-sm text-muted-foreground line-clamp-2">{p.summary}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
